import React, { useEffect, useMemo, useState } from 'react';
import { AccessibilityInfo, ActivityIndicator, Platform, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { DateTime } from 'luxon';
import Chip from '@/components/General/Chip';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import MilestoneBadge from '@/components/Milestones/MilestoneBadge';
import MilestoneCelebrationModal from '@/components/Milestones/MilestoneCelebrationModal';
import { Query } from '@/utils/react-query/Queries';
import { getMilestoneAwardReplay } from '@/data/milestones';
import { MILESTONES_ENABLED } from '@/config/milestones';
import { groupMilestonesBySeries, milestoneBadgeLabel, nextMilestoneThreshold, normalizeAwardReplay } from '@/utils/milestones';
import { canViewPersonMilestones } from '@/utils/milestonePermissions';

/**
 * Profile "Badges" scene: grouped earned badges, visible earned-threshold
 * count, next-tier progress, and a manual replay detail popup.
 *
 * The server hides financial awards before computing counts — this renders
 * exactly what it is given. Queries only run while the tab is mounted (the
 * profile TabView renders scenes lazily) and `MILESTONES_ENABLED` is on.
 */
const PersonMilestonesTab = ({ personID, isOwnProfile = false }) => {
    const { data: loggedInUser } = Query.people.loggedInUser();
    const [replay, setReplay] = useState(null);
    const [replaying, setReplaying] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);

    // Same reduced-motion signal the celebration provider uses — replay
    // shows the identical static fullscreen surface.
    useEffect(() => {
        if (Platform.OS === 'web') {
            if (typeof window === 'undefined') return undefined;
            const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
            setReducedMotion(query?.matches === true);
            const onChange = (event) => setReducedMotion(!!event.matches);
            query?.addEventListener?.('change', onChange);
            return () => query?.removeEventListener?.('change', onChange);
        }
        AccessibilityInfo.isReduceMotionEnabled().then(setReducedMotion).catch(() => {});
        const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', setReducedMotion);
        return () => subscription?.remove();
    }, []);

    const allowed = canViewPersonMilestones(loggedInUser, personID);
    const stateQuery = Query.milestones.person({
        organizationID: loggedInUser?.organizationID,
        viewerAccountID: loggedInUser?.accountID,
        personID,
        options: { enabled: MILESTONES_ENABLED && allowed && !!loggedInUser?.organizationID && !!personID },
    });
    const { data, isLoading, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage } = stateQuery;

    const groups = useMemo(
        () => groupMilestonesBySeries({ series: (data?.pages ?? []).flatMap(page => page?.series ?? []) }),
        [data]
    );
    const earnedCount = (data?.pages ?? []).reduce((total, page) => total + (Number(page?.earnedThresholdCount) || 0), 0);
    const trackingStartedAt = (data?.pages ?? []).find(p => p?.trackingStartedAt)?.trackingStartedAt;

    const openReplay = async (award) => {
        if (!award?.id || replaying) return;
        setReplaying(true);
        try {
            const snapshot = await getMilestoneAwardReplay(award.id);
            const entry = normalizeAwardReplay(snapshot);
            if (entry) setReplay(entry);
        } catch {
            // getMilestoneAwardReplay already toasts MILESTONE_* errors.
        } finally {
            setReplaying(false);
        }
    };

    if (!MILESTONES_ENABLED) return null;

    if (!allowed) {
        return <EmptyListPlaceholder noLottie spacing={48} message="You don't have permission to view badges." />;
    }

    if (isLoading) {
        return <View style={styles.center}><ActivityIndicator /></View>;
    }

    if (isError) {
        const denied = error?.milestoneCode === 'MILESTONE_PERMISSION_DENIED';
        return (
            <EmptyListPlaceholder
                noLottie
                spacing={48}
                message={denied ? "You don't have permission to view badges." : "Badges couldn't be loaded right now. Try again in a moment."}
            />
        );
    }

    const anyAwards = groups.some(g => g.awards.length > 0);
    const anySeries = groups.length > 0;

    return (
        <ScrollView style={styles.scroll} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
            <View style={styles.countRow}>
                <Unicon name="trophy-outline" size={18} colorVariant="gold" />
                <Text style={styles.countText} testID="milestone-earned-count">
                    {earnedCount} milestone{earnedCount === 1 ? '' : 's'} completed
                </Text>
            </View>

            {!anySeries && (
                <EmptyListPlaceholder
                    noLottie
                    spacing={48}
                    message="Milestones aren't set up yet. An administrator can enable series from the organization settings."
                />
            )}
            {anySeries && !anyAwards && (
                <EmptyListPlaceholder
                    type="complete"
                    spacing={48}
                    message={isOwnProfile ? "No badges earned yet — keep going!" : "No badges earned yet."}
                />
            )}

            {groups.map(group => {
                const archived = !!group.deletedAt;
                const current = group.progress?.currentValue != null ? Number(group.progress.currentValue) : null;
                const nextTier = group.progress?.nextThreshold != null
                    ? Number(group.progress.nextThreshold)
                    : nextMilestoneThreshold(group.thresholds ?? [], current ?? 0)?.threshold ?? null;
                const progressPct = current != null && nextTier ? Math.min(100, Math.round((current / nextTier) * 100)) : null;

                return (
                    <View key={group.milestoneID ?? group.name} style={styles.seriesCard}>
                        <View style={styles.seriesHeader}>
                            <Text style={styles.seriesName} numberOfLines={1}>{group.name}</Text>
                            {archived
                                ? <Chip colorVariant="warning" icon="archive-outline">Archived</Chip>
                                : !group.enabled && <Chip colorVariant="textSecondary" icon="time-outline">Historical</Chip>}
                        </View>

                        <View style={styles.badgeRow}>
                            {group.awards.map(award => (
                                <MilestoneBadge
                                    key={award.id ?? award.thresholdID ?? award.threshold}
                                    testID={`milestone-badge-${award.id ?? award.thresholdID}`}
                                    icon={award.badgeIcon ?? group.badgeIcon}
                                    colorToken={award.badgeColorToken ?? group.badgeColorToken}
                                    threshold={award.threshold}
                                    seriesName={group.name}
                                    emphasized={Number(award.threshold) === group.highestThreshold}
                                    historical={archived}
                                    onPress={() => openReplay(award)}
                                    accessibilityLabel={milestoneBadgeLabel({ seriesName: group.name, threshold: award.threshold })}
                                />
                            ))}
                            {current != null && nextTier != null && !archived && (
                                <MilestoneBadge
                                    icon={group.badgeIcon}
                                    colorToken={group.badgeColorToken}
                                    threshold={nextTier}
                                    seriesName={group.name}
                                    earned={false}
                                    accessibilityLabel={`${group.name} next milestone at ${nextTier}`}
                                />
                            )}
                        </View>

                        {current != null && nextTier != null && !archived && (
                            <View style={styles.progressBlock}>
                                <View style={styles.progressTrack}>
                                    <View style={styles.progressFill(progressPct ?? 0)} />
                                </View>
                                <Text style={styles.progressLabel}>{current} / {nextTier}</Text>
                            </View>
                        )}

                        {group.usesTrackingStart && trackingStartedAt && (
                            <Text style={styles.trackingNote}>Counts days active since {DateTime.fromISO(trackingStartedAt).toLocaleString(DateTime.DATE_MED)}.</Text>
                        )}
                    </View>
                );
            })}

            {hasNextPage && (
                <SolidButton
                    testID="milestone-badges-more"
                    variant="outlined"
                    type="textSecondary"
                    text={isFetchingNextPage ? 'Loading…' : 'Load More'}
                    processing={isFetchingNextPage}
                    onPress={() => fetchNextPage()}
                />
            )}

            {/* Manual replay uses the shared achievement toast, without a lease. */}
            <MilestoneCelebrationModal
                visible={!!replay}
                items={replay ? [replay] : []}
                mode="replay"
                reducedMotion={reducedMotion}
                testID="milestone-badge-detail"
                dismissTestID="milestone-badge-detail-close"
                onDismiss={() => setReplay(null)}
            />
        </ScrollView>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, shadows }) => ({
    scroll: {
        flex: 1,
    },
    content: {
        padding: spacing.md,
        gap: spacing.md,
    },
    center: {
        paddingVertical: spacing.xl,
        alignItems: 'center',
    },
    countRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    countText: {
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: 16,
    },
    seriesCard: {
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: spacing.md,
        boxShadow: shadows.card,
        gap: spacing.sm,
    },
    seriesHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.sm,
    },
    seriesName: {
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: 15,
        flexShrink: 1,
    },
    badgeRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.md,
        paddingTop: spacing.xs,
    },
    progressBlock: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    progressTrack: {
        flex: 1,
        height: 6,
        borderRadius: 3,
        backgroundColor: colors.surfaceVariant,
        overflow: 'hidden',
    },
    progressFill: (pct) => ({
        width: `${pct}%`,
        height: '100%',
        borderRadius: 3,
        backgroundColor: colors.gold,
    }),
    progressLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 11,
    },
    trackingNote: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: 11,
        fontStyle: 'italic',
    },
}));

export default PersonMilestonesTab;
